import { useCallback, useEffect, useState } from "react";
import * as api from "../api.js";
import { useCelular, useEstado } from "../estado.jsx";
import { useNavegacao } from "../navegacao.jsx";
import { ATALHO_DA_PALETA } from "../visual.jsx";
import { montarPassos, paletaJaUsada } from "../primeiros-passos.js";
import { useWhatsapp } from "./AvisoDoWhatsapp.jsx";
import { Botao, Progresso } from "./comuns.jsx";

/**
 * Os primeiros passos desta pessoa, relidos quando o dado que os cumpre muda.
 *
 * O servidor responde pelo banco; o que faz a tela reler é o próprio dado da
 * mesa mudando — o WhatsApp que acabou de conectar, a pessoa que entrou na
 * equipe — e a volta à aba (quem foi conectar o aparelho no celular volta e
 * encontra o passo marcado, sem recarregar a página).
 */
export function usePrimeirosPassos() {
  const { equipe } = useEstado();
  const wa = useWhatsapp();
  const celular = useCelular();
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState(null);

  const recarregar = useCallback(() => {
    api.primeirosPassos().then(
      (r) => {
        setDados(r);
        setErro(null);
      },
      (e) => setErro(e.message),
    );
  }, []);

  const pessoas = equipe?.length ?? 0;
  useEffect(() => {
    recarregar();
  }, [recarregar, wa.conectado, pessoas]);

  useEffect(() => {
    const voltou = () => document.visibilityState === "visible" && recarregar();
    document.addEventListener("visibilitychange", voltou);
    return () => document.removeEventListener("visibilitychange", voltou);
  }, [recarregar]);

  const resumo = montarPassos(dados, { paletaUsada: paletaJaUsada(), celular, atalho: ATALHO_DA_PALETA });
  return { dados, erro, recarregar, ...resumo };
}

/**
 * O cartão da boas-vindas: quanto falta e o próximo passo, com a porta para a
 * lista inteira.
 *
 * Widget que aparece quando precisa: com tudo cumprido ele NÃO EXISTE — faixa de
 * onboarding que fica para sempre é ruído pagando por um proveito de uma semana.
 * E também não aparece enquanto a leitura não chegou: um "0 de 6" piscando antes
 * do dado é a tela afirmando o que não sabe.
 */
export function CartaoDosPrimeirosPassos() {
  const { ir } = useNavegacao();
  const { dados, total, feitos, proximo, completos } = usePrimeirosPassos();
  if (!dados || !total || completos) return null;

  return (
    <div className="mb-6 w-full max-w-[420px] rounded-2xl border border-ambar/32 bg-ambar/6 p-[14px_16px] text-left">
      <Progresso
        feitos={feitos}
        total={total}
        base={0}
        rotulo={dados.tipo === "configurar" ? "O SETOR DE PÉ" : "SEUS PRIMEIROS PASSOS"}
        nota={`Falta${total - feitos > 1 ? "m" : ""} ${total - feitos} de ${total}. O próximo: “${proximo.rotulo}”.`}
      />
      <div className="mt-3 flex flex-wrap gap-2">
        <Botao rotulo="Continuar" destaque aoTocar={() => ir("/comecar")} />
      </div>
    </div>
  );
}
